import {useCallback, useEffect, useRef, useState} from 'react';
import {safeCaptureException} from '../../../utils/sentry';
import {API, BiomatchResponse, CompareBiomatchResponse} from '@chekin/api';
import {IV_DOCUMENT_IMAGE_TYPES, OCRDetectedData} from '@chekin/core';
import {
  ANALYTIC_EVENTS,
  IV_ERROR_CATEGORY,
  IVErrorCategory,
} from '../../../analytics/constants';
import {Analytics} from '../../../analytics/Analytics';
import {
  getBiomatchDocumentPictureId,
  omitRawImage,
} from '../../../hooks/iv/documentSelfieDetection/helpers';
import type {
  IVFailureDetails,
  NextStepDetails,
  StoredImageDetails,
} from '../../../hooks/iv/documentSelfieDetection/types';
import {useBiomatchFlow} from '../../../hooks/iv/documentSelfieDetection/useBiomatchFlow';
import {useIVCompletionFlow} from '../../../hooks/iv/documentSelfieDetection/useIVCompletionFlow';
import {useIVProcessState} from '../../../hooks/iv/documentSelfieDetection/useIVProcessState';
import {useLivenessVideoUpload} from '../../../hooks/iv/documentSelfieDetection/useLivenessVideoUpload';
import {useOCRFlow} from '../../../hooks/iv/documentSelfieDetection/useOCRFlow';
import {isServiceFailure, isServiceUnavailable} from '../../../hooks/iv/serviceFailure';
import {useUpdateIvIntent} from '../../../hooks/useUpdateIvIntent';
import {getDocumentNationality} from '../../../store/selectors/getDocumentNationality';
import {getDocumentType} from '../../../store/selectors/getDocumentType';
import {useIVStore} from '../../../store/useIVStore';
import {getDetectedIvBypassAge} from '../ivBypass';
import {saveBiomatchComparisonSuccess} from './saveBiomatchComparisonSuccess';
import {useGuestSchema} from '../GuestSchemaContext';
import type {CaptureCheck} from '../../../components/capture/CaptureCheckingView';

export type UseIVDetectionFlowOptions = {
  docType?: IV_DOCUMENT_IMAGE_TYPES;
  onlyScanning?: boolean;
  livenessVideo?: Blob | null;
  biomatch: boolean;
  countryCode?: string;
  isCountryLoaded?: boolean;
  reservationId?: string;
  getIntentId?: () => string | null | undefined;
  getNextStepDetails: () => NextStepDetails;
  navigateTo: (to: string) => void;
  onAfterSuccessDelay?: (nextStep: NextStepDetails, postHandler: () => void) => boolean;
  onBeforeFinish?: () => boolean;
  onOCRData?: (data: OCRDetectedData) => void;
  isHardwareScanning?: boolean;
  onError?: (error: Error | string) => void;
  resetScroll?: () => void;
};

function useIVDetectionFlow({
  docType,
  onlyScanning,
  livenessVideo,
  biomatch,
  countryCode,
  isCountryLoaded = true,
  reservationId,
  getIntentId,
  getNextStepDetails,
  navigateTo,
  onAfterSuccessDelay,
  onBeforeFinish,
  onOCRData,
  isHardwareScanning,
  onError,
  resetScroll,
}: UseIVDetectionFlowOptions) {
  const {sendUpdateIvIntentRequestAsync} = useUpdateIvIntent();
  const {formFields} = useGuestSchema();
  const {
    setDistanceBiomatchComparing,
    setOCRDocumentPassed,
    setBiomatchPassed,
    setImageDetails,
    setDeclaredIvBypassAge,
    imagesDetails,
  } = useIVStore();
  const documentType = useIVStore(getDocumentType);
  const nationality = useIVStore(getDocumentNationality);
  const frontSideImage = useIVStore(state => state.imagesDetails.front);
  const livenessResultData = useIVStore(state => state.livenessResultData);
  const setLivenessResultData = useIVStore(state => state.setLivenessResultData);
  const isIvFailed = useIVStore(state => state.isIvFailed);

  const hasReportedMissingCountry = useRef(false);
  const [passedChecks, setPassedChecks] = useState<CaptureCheck[]>([]);

  const markCheckPassed = useCallback((check: CaptureCheck) => {
    setPassedChecks(prev => (prev.includes(check) ? prev : [...prev, check]));
  }, []);

  const startingChecks = useCallback(
    <A extends unknown[]>(send: (...args: A) => void) =>
      (...args: A) => {
        setPassedChecks([]);
        send(...args);
      },
    [],
  );
  useEffect(() => {
    if (!isCountryLoaded || countryCode || hasReportedMissingCountry.current) {
      return;
    }
    hasReportedMissingCountry.current = true;
    safeCaptureException('countryCode is missing');
  }, [countryCode, isCountryLoaded]);

  const isBiomatchAndPassport = biomatch && !documentType?.two_sides;

  const processStateController = useIVProcessState({
    isHardwareScanning,
    onError,
    resetScroll,
  });

  const {handleFinish} = useIVCompletionFlow({
    getNextStepDetails,
    setProcessState: processStateController.setProcessState,
    navigateTo,
    onAllStepsCompleted: () => {
      Analytics.track(ANALYTIC_EVENTS.ivFinishingAllSuccessfully);
    },
    onAfterSuccessDelay,
  });

  const {uploadLivenessVideo} = useLivenessVideoUpload({
    onUploadFinished: value => {
      setLivenessResultData({livenessVideoUrl: value});
    },
  });

  const finishFlow = useCallback(() => {
    if (onBeforeFinish?.()) return;
    handleFinish();
  }, [handleFinish, onBeforeFinish]);

  const saveImageDetails = useCallback(
    async (nextDocType: IV_DOCUMENT_IMAGE_TYPES, details: StoredImageDetails) => {
      setImageDetails(details);
      const intentId = getIntentId?.();
      if (!intentId) return;

      await sendUpdateIvIntentRequestAsync({
        id: intentId,
        image_details: {...imagesDetails, [nextDocType]: omitRawImage(details)},
      });
    },
    [getIntentId, imagesDetails, sendUpdateIvIntentRequestAsync, setImageDetails],
  );

  const saveOCRData = useCallback(
    async (data: OCRDetectedData) => {
      markCheckPassed('lines');
      if (!onlyScanning) {
        const intentId = getIntentId?.();
        if (intentId) {
          await sendUpdateIvIntentRequestAsync({id: intentId, document_passed: true});
        }
        setOCRDocumentPassed(data?.mrz_detected || data?.barcode_detected);
        const detectedBypassAge = getDetectedIvBypassAge(data, formFields);
        if (detectedBypassAge != null) {
          setDeclaredIvBypassAge(detectedBypassAge);
          const biomatchPictureId = getBiomatchDocumentPictureId(
            useIVStore.getState().imagesDetails.front,
          );
          if (biomatchPictureId) {
            API.identityVerification
              .deleteDocumentPicture(biomatchPictureId)
              .catch(error =>
                safeCaptureException(error, 'Failed to delete minor document picture'),
              );
          }
        }
      }
      onOCRData?.(data);
    },
    [
      formFields,
      getIntentId,
      markCheckPassed,
      onOCRData,
      onlyScanning,
      sendUpdateIvIntentRequestAsync,
      setDeclaredIvBypassAge,
      setOCRDocumentPassed,
    ],
  );

  const saveComparisonSuccess = useCallback(
    async (data: CompareBiomatchResponse) => {
      markCheckPassed('compare');
      const intentId = getIntentId?.();

      setBiomatchPassed(data.is_match);
      setDistanceBiomatchComparing(Number(data.distance?.toFixed(1) ?? 0.0));

      await saveBiomatchComparisonSuccess({
        livenessVideo,
        intentId,
        livenessResultData,
        isIvFailed,
        uploadLivenessVideo,
        updateIvIntent: sendUpdateIvIntentRequestAsync,
      });
    },
    [
      getIntentId,
      isIvFailed,
      livenessResultData,
      markCheckPassed,
      livenessVideo,
      sendUpdateIvIntentRequestAsync,
      setBiomatchPassed,
      setDistanceBiomatchComparing,
      uploadLivenessVideo,
    ],
  );

  const saveBiomatchImageDetails = useCallback(
    async (nextDocType: IV_DOCUMENT_IMAGE_TYPES, details: StoredImageDetails) => {
      markCheckPassed('face');
      await saveImageDetails(nextDocType, details);
    },
    [markCheckPassed, saveImageDetails],
  );

  const handleBiomatchRetriesFinish = useCallback(
    (data: CompareBiomatchResponse | BiomatchResponse) => {
      const isCompare = 'is_match' in data;
      Analytics.track(ANALYTIC_EVENTS.ivRetryingLimitOver, {
        check_id: data.id,
        status: data.status,
        picture_file: (data as BiomatchResponse).picture_file,
        is_match: Boolean((data as CompareBiomatchResponse).is_match),
        type: 'Biomatch',
        error_category: isCompare
          ? IV_ERROR_CATEGORY.faceMismatch
          : IV_ERROR_CATEGORY.faceNotDetected,
      });
      processStateController.handleCheckTimeout();
    },
    [processStateController],
  );

  const handleOCRRetriesFinish = useCallback(
    (data: OCRDetectedData) => {
      Analytics.track(ANALYTIC_EVENTS.ivRetryingLimitOver, {
        check_id: data.id,
        status: data.status,
        picture_file: data.picture_file,
        mrz_detected: Boolean(data.mrz_detected),
        barcode_detected: Boolean(data.barcode_detected),
        type: 'OCR',
        error_category: IV_ERROR_CATEGORY.documentNotRecognized,
      });
      processStateController.handleCheckTimeout();
    },
    [processStateController],
  );

  const handleFailed = useCallback(
    (
      nextErrorMessage?: string,
      _image?: string,
      category?: IVErrorCategory,
      details?: IVFailureDetails,
    ) => {
      Analytics.track(ANALYTIC_EVENTS.ivError, {
        'Error message': nextErrorMessage ?? '',
        'Image type': docType,
        error_category: category ?? IV_ERROR_CATEGORY.unknown,
      });
      processStateController.setFailureState(nextErrorMessage, details);
    },
    [docType, processStateController],
  );

  const ocrFlow = useOCRFlow({
    context: {
      docType,
      onlyScanning,
      isBiomatchAndPassport,
      documentType,
      frontSideImage,
      countryCode,
      reservationId,
      nationality,
      intentId: getIntentId?.() ?? undefined,
    },
    actions: {saveOCRData, saveOCRImageDetails: saveImageDetails, finishFlow},
    handlers: {
      onError: processStateController.handleError,
      onBefore: processStateController.onBefore,
      onRetryLimitReached: handleOCRRetriesFinish,
      onFailed: handleFailed,
    },
  });

  const biomatchFlow = useBiomatchFlow({
    context: {
      docType,
      documentType,
      frontSideImage,
      reservationId,
      countryCode,
      nationality,
      isBiomatchAndPassport,
    },
    actions: {
      saveBiomatchImageDetails,
      saveComparisonSuccess,
      runOCR: ocrFlow.sendAndVerifyImageForOCR,
      finishFlow,
    },
    handlers: {
      onError: processStateController.handleError,
      onBefore: processStateController.onBefore,
      onRetryLimitReached: handleBiomatchRetriesFinish,
      onFailed: handleFailed,
    },
  });

  const sendAndReadImageForOCR = startingChecks(ocrFlow.sendAndVerifyImageForOCR);
  const sendSelfieToDetectFace = startingChecks(
    biomatchFlow.sendAndVerifySelfieForFaceDetection,
  );
  const sendFrontSideDocument = startingChecks(
    biomatch || documentType?.two_sides
      ? biomatchFlow.sendAndVerifyDocumentToBiomatchAndOCR
      : ocrFlow.sendAndVerifyImageForOCR,
  );
  const sendBackSideDocument = startingChecks(ocrFlow.sendAndVerifyImageForOCR);

  return {
    sendSelfieToDetectFace,
    sendAndReadImageForOCR,
    sendFrontSideDocument,
    sendBackSideDocument,
    handleFinish,
    isProcessing: processStateController.isProcessModalOpen,
    processState: processStateController.processState,
    passedChecks,
    errorMessage: processStateController.errorMessage,
    errorDescription: processStateController.errorDescription,
    errorReason: processStateController.errorReason,
    failureCount: processStateController.failureCount,
    isServiceFailure: isServiceFailure(processStateController.errorReason),
    isServiceUnavailable: isServiceUnavailable(processStateController.errorReason),
    onTryAgain: processStateController.closeAndCleanLoadingModal,
    handleError: processStateController.handleError,
  };
}

export {useIVDetectionFlow};
